import { IconCheck, IconInfoOutline, IconWarningOutline } from '../../icons';
import cn from '../../utils/cn';

import React, { createContext, useContext } from 'react';

export enum AlertVariant {
  DEFAULT,
  INFO,
  WARNING,
  DESTRUCTIVE,
  SUCCESS,
  BRAND,
}

export enum AlertSize {
  SMALL,
  MEDIUM,
  LARGE,
}

type AlertContextType = {
  variant: AlertVariant;
  size: AlertSize;
};

const AlertContext = createContext<AlertContextType>({
  variant: AlertVariant.INFO,
  size: AlertSize.MEDIUM,
});

export function useAlertContext() {
  const context = useContext(AlertContext);

  if (!context) {
    throw new Error('useAlertContext must be used within an Alert component');
  }

  return context;
}

type AlertProps = {
  variant?: AlertVariant;
  size?: AlertSize;
} & React.ComponentPropsWithoutRef<'div'>;

function Alert({
  variant = AlertVariant.INFO,
  size = AlertSize.MEDIUM,
  children,
  className,
  ...props
}: AlertProps) {
  return (
    <Root variant={variant} size={size} className={className} {...props}>
      <Icon />

      {children}
    </Root>
  );
}

function Root({
  variant = AlertVariant.INFO,
  size = AlertSize.MEDIUM,
  children,
  className,
  ...props
}: AlertProps) {
  const variantClasses = {
    [AlertVariant.DEFAULT]: 'bg-white text-neutral-700 ring-neutral-300',
    [AlertVariant.INFO]: 'bg-blue-100 text-blue-500 ring-blue-300',
    [AlertVariant.WARNING]: 'bg-yellow-100 text-yellow-500 ring-yellow-300',
    [AlertVariant.DESTRUCTIVE]: 'bg-red-100 text-red-500 ring-red-300',
    [AlertVariant.SUCCESS]: 'bg-green-100 text-green-500 ring-green-300',
    [AlertVariant.BRAND]: 'bg-white text-brand ring-brand/30',
  } satisfies Record<AlertVariant, string>;

  const sizeClasses = {
    [AlertSize.SMALL]: 'px-2.5 py-2 text-xs gap-2',
    [AlertSize.MEDIUM]: 'px-4 py-2.5 text-sm gap-2.5',
    [AlertSize.LARGE]: 'px-4 py-3 text-base gap-2.5',
  } satisfies Record<AlertSize, string>;

  return (
    <AlertContext.Provider value={{ variant, size }}>
      <div
        role="alert"
        className={cn(
          'flex items-center rounded-lg font-medium ring-1 ring-inset',
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </AlertContext.Provider>
  );
}

type IconProps = {
  children?: React.ReactElement<React.ComponentProps<'svg'>>;
};

function Icon({ children }: IconProps) {
  const { variant, size } = useAlertContext();
  const defaultIconMap = {
    [AlertVariant.DEFAULT]: <IconInfoOutline />,
    [AlertVariant.INFO]: <IconInfoOutline />,
    [AlertVariant.WARNING]: <IconWarningOutline />,
    [AlertVariant.DESTRUCTIVE]: <IconWarningOutline />,
    [AlertVariant.SUCCESS]: <IconCheck />,
    [AlertVariant.BRAND]: <IconInfoOutline />,
  } satisfies Record<AlertVariant, React.ReactNode>;
  const Icon = children ? React.Children.only(children) : defaultIconMap[variant];

  const variantClasses = {
    [AlertVariant.DEFAULT]: 'text-neutral-700',
    [AlertVariant.INFO]: 'text-blue-500',
    [AlertVariant.WARNING]: 'text-yellow-500',
    [AlertVariant.DESTRUCTIVE]: 'text-red-500',
    [AlertVariant.SUCCESS]: 'text-green-500',
    [AlertVariant.BRAND]: 'text-brand',
  } satisfies Record<AlertVariant, string>;

  const sizeClasses = {
    [AlertSize.SMALL]: 'w-4 h-4',
    [AlertSize.MEDIUM]: 'w-4 h-4',
    [AlertSize.LARGE]: 'w-5 h-5',
  } satisfies Record<AlertSize, string>;

  return React.cloneElement(Icon, {
    className: cn(
      'shrink-0',
      variantClasses[variant],
      sizeClasses[size],
      typeof Icon.props.className === 'string' ? Icon.props.className : '',
    ),
  });
}

Alert.Root = Root;
Alert.Icon = Icon;

export default Alert;
